import { Divider, FormControl, FormLabel, HStack, Input, Stack, useToast } from "@chakra-ui/react";
import CDS from "design-system";
import { useState } from "react";
import GoogleIcon from "assets/GoogleIcon";
import { API_ROUTES } from "definitions/constants/routeConstants";
import apiClient from "services/ApiClient";
import { isNetworkingError } from "services/ApiClient/networkingError";

interface SignInOptionsFormProps {
  email: string;
  ssoUrl?: string;
  ssoProviderName?: string;
  ssoEnforced?: boolean;
  emailType?: "work" | "personal";
  onMagicLinkSent?: () => void;
  onClose: () => void;
}

export default function SignInOptionsForm({
  email,
  ssoUrl,
  ssoProviderName,
  ssoEnforced = false,
  emailType = "work",
  onMagicLinkSent,
  onClose,
}: SignInOptionsFormProps) {
  const toast = useToast();
  const [password, setPassword] = useState("");
  const [emailValue, setEmailValue] = useState(email);
  const [isLoading, setIsLoading] = useState(false);

  // The bypass-SSO ("new hire, not yet started") path opens this modal without an
  // email, so collect one here before magic-link/password auth can run.
  const needsEmail = !email.trim();
  const trimmedEmail = emailValue.trim();

  const showPasswordAuth = ["joincamino.com", "zoom.us", "okta.com"].some((domain) => trimmedEmail.endsWith(domain));

  // Pre-start new hires get a personal-email magic link only — no SSO/password/
  // Google. Work sign-in keeps the full prod option set.
  const isPersonal = emailType === "personal";

  const handleSSOLogin = () => {
    if (ssoUrl) {
      window.location.href = ssoUrl;
    }
  };

  const handleMagicLink = async () => {
    if (!trimmedEmail) return;

    setIsLoading(true);
    try {
      await apiClient.post(API_ROUTES.sessions.magicLinks.base, { email: trimmedEmail, emailType });
      if (onMagicLinkSent) {
        onMagicLinkSent();
      }
      onClose();
    } catch (error) {
      const errorMessage =
        isNetworkingError(error) && error.errors.length > 0
          ? error.errors[0]
          : "Please ensure you have entered a valid email address.";

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async () => {
    setIsLoading(true);
    try {
      await apiClient.post(API_ROUTES.sessions.passwords.base, { email: trimmedEmail, password });
      // Successful password login - reload the page to redirect to authenticated state
      window.location.reload();
    } catch (error) {
      const errorMessage =
        isNetworkingError(error) && error.errors.length > 0
          ? error.errors[0]
          : "Invalid email or password. Please try again.";

      toast({
        title: "Login Failed",
        description: errorMessage,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const params = new URLSearchParams({
      client_id: process.env.GOOGLE_OAUTH_CLIENT_ID || "",
      redirect_uri: `${window.location.origin}/api/v1/oauth/google/auth/callback`,
      response_type: "code",
      scope: "https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile",
      access_type: "online",
    });

    window.location.href = `https://accounts.google.com/o/oauth2/auth?${params.toString()}`;
  };

  const getSSOProviderDisplayName = () => {
    if (!ssoProviderName) return "SSO";
    const providerNames: Record<string, string> = {
      okta: "Okta",
      azure_ad: "Microsoft Azure AD",
      google_workspace: "Google Workspace",
      onelogin: "OneLogin",
    };
    return providerNames[ssoProviderName] || "SSO";
  };

  return (
    <Stack spacing="4" pb="6">
      {ssoEnforced ? (
        <>
          <CDS.Text12 color="gray.600">
            Your organization requires you to sign in using {getSSOProviderDisplayName()}.
          </CDS.Text12>
          <CDS.Button size={CDS.Button.size.lg} onClick={handleSSOLogin}>
            Continue with {getSSOProviderDisplayName()}
          </CDS.Button>
        </>
      ) : isPersonal ? (
        <>
          <CDS.Callout variant="info">
            This only works before your start date. If you already have a work email, use “I have my work email”
            instead.
          </CDS.Callout>
          <FormControl>
            <FormLabel htmlFor="signin-email">Email</FormLabel>
            <Input
              id="signin-email"
              type="email"
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
              placeholder="Enter your email"
              onKeyDown={(e) => {
                if (e.key === "Enter" && trimmedEmail) {
                  handleMagicLink();
                }
              }}
            />
          </FormControl>
          <CDS.Button
            size={CDS.Button.size.lg}
            variant={CDS.Button.variant.outline}
            onClick={handleMagicLink}
            disabled={!trimmedEmail}
            loading={isLoading}
          >
            Continue with Magic Link
          </CDS.Button>
        </>
      ) : (
        <>
          {needsEmail && (
            <>
              <CDS.Callout variant="info">
                This only works before your start date. If you already have a work email, use “I have my work email”
                instead.
              </CDS.Callout>
              <FormControl>
                <FormLabel htmlFor="signin-email">Email</FormLabel>
                <Input
                  id="signin-email"
                  type="email"
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  placeholder="Enter your email"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && trimmedEmail) {
                      handleMagicLink();
                    }
                  }}
                />
              </FormControl>
            </>
          )}

          {ssoUrl && (
            <>
              <CDS.Button size={CDS.Button.size.lg} onClick={handleSSOLogin}>
                Continue with {getSSOProviderDisplayName()}
              </CDS.Button>

              <HStack>
                <Divider />
                <CDS.Text12 color="gray.500" whiteSpace="nowrap">
                  or
                </CDS.Text12>
                <Divider />
              </HStack>
            </>
          )}

          <CDS.Button
            leftIcon={{ customIcon: <GoogleIcon /> }}
            onClick={handleGoogleLogin}
            intent={CDS.Button.intent.secondary}
            variant={CDS.Button.variant.outline}
            size={CDS.Button.size.lg}
          >
            Continue with Google
          </CDS.Button>

          {showPasswordAuth && (
            <>
              <FormControl>
                <FormLabel htmlFor="password">Password</FormLabel>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && password.trim()) {
                      handlePasswordLogin();
                    }
                  }}
                />
              </FormControl>
              <CDS.Button
                size={CDS.Button.size.lg}
                variant={CDS.Button.variant.outline}
                onClick={handlePasswordLogin}
                disabled={!password.trim()}
                loading={isLoading}
              >
                Continue with Password
              </CDS.Button>
            </>
          )}

          <HStack>
            <Divider />
            <CDS.Text12 color="gray.500" whiteSpace="nowrap">
              or
            </CDS.Text12>
            <Divider />
          </HStack>

          <CDS.Button
            size={CDS.Button.size.lg}
            variant={CDS.Button.variant.outline}
            onClick={handleMagicLink}
            disabled={!trimmedEmail}
            loading={isLoading}
          >
            Continue with Magic Link
          </CDS.Button>
        </>
      )}
    </Stack>
  );
}
